import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { skipToken } from '@reduxjs/toolkit/query/react'
import Style from './createConnector.module.css'
import WizardShell from './WizardShell'
import ChooseSystemStep from './ChooseSystemStep'
import ChoosePartnerStep, { partnerOf } from './ChoosePartnerStep'
import PlanStep from './PlanStep'
import DetailsStep from './DetailsStep'
import useCreateConnector from './useCreateConnector'
import ConnectorLogo from '../../SharedComponents/ConnectorCard/ConnectorLogo'
import PageLoader from '../../SharedComponents/PageState/PageLoader'
import PageNotice from '../../SharedComponents/PageState/PageNotice'
import { connectorIconSrc } from '../../SharedComponents/ConnectorIcon/ConnectorIcon'
import {
    useGetConnectorFeaturesQuery,
    useGetConnectorQuestionsQuery,
    useGetEgeIntegrationsQuery,
    useGetEndpointIntegrationsQuery,
    useGetMarketplaceEndpointsQuery,
    useLazyGetConnectorQuestionsQuery
} from '../../../features/marketplace/marketplaceApi'
import { describeSystem } from '../../../features/marketplace/connectorCatalog'
import { useConnectionCreateAccess } from '../../../features/clientUsers/useClientAccess'
import { PATH, paths } from '../../../app/routes'

const EGE = 'ege'
const DETAILS = 'details'
const EMPTY = []
const EMPTY_PLAN = { sourceVersion: null, targetVersion: null, productVersionId: null }
const STEP = { SYSTEM: 0, PARTNER: 1, PLAN: 2, DETAILS: 3 }

const bySystemOrder = (a, b) => (Number(b.isAvailable) - Number(a.isAvailable)) || a.name.localeCompare(b.name)

const fallbackSystem = (shortName) => ({
    id: shortName,
    shortName,
    name: shortName,
    categoryLabel: '',
    image: connectorIconSrc(shortName),
    isAvailable: true
})

const PairStrip = ({ integration, systemFor }) => (
    <div className={Style.pair} aria-label="Selected systems">
        {[['Source', integration.source.shortName], ['Target', integration.target.shortName]].map(([role, shortName], index) => {
            const system = systemFor(shortName)
            return (
                <React.Fragment key={role}>
                    {index === 1 ? <i className={`uil-arrow-right ${Style.pairArrow}`} aria-hidden="true" /> : null}
                    <span className={Style.pairSystem}>
                        <ConnectorLogo src={system.image} name={shortName} size="sm" />
                        <span className={Style.pairText}>
                            <span className={Style.pairRole}>{role}</span>
                            <span className={Style.pairName}>{system.name}</span>
                        </span>
                    </span>
                </React.Fragment>
            )
        })}
    </div>
)

const CreateConnectorWizard = () => {
    const history = useHistory()
    const params = new URLSearchParams(useLocation().search)
    const systemParam = params.get('system')
    const integrationParam = params.get('integration')
    const from = params.get('from')
    const isDetails = params.get('step') === DETAILS

    const current = integrationParam ? (isDetails ? STEP.DETAILS : STEP.PLAN) : systemParam ? STEP.PARTNER : STEP.SYSTEM
    const firstId = integrationParam ? from : systemParam

    const endpointsQuery = useGetMarketplaceEndpointsQuery()
    const systems = useMemo(() => (endpointsQuery.data || EMPTY).map(describeSystem).sort(bySystemOrder), [endpointsQuery.data])
    const systemFor = useCallback((shortName) => systems.find((system) => system.shortName === shortName) || fallbackSystem(shortName), [systems])
    const firstSystem = systems.find((system) => String(system.id) === String(firstId)) || null

    const integrationsQuery = useGetEndpointIntegrationsQuery(firstId && firstId !== EGE ? firstId : skipToken)
    const egeQuery = useGetEgeIntegrationsQuery(from === EGE ? undefined : skipToken)
    const integrationList = (from === EGE ? egeQuery.data : integrationsQuery.data) || EMPTY
    const integration = integrationList.find((item) => String(item.id) === String(integrationParam)) || null

    const [draftSystemId, setDraftSystemId] = useState(systemParam)
    const [partnerShortName, setPartnerShortName] = useState(null)
    const [draftIntegrationId, setDraftIntegrationId] = useState(null)
    const [plan, setPlan] = useState(EMPTY_PLAN)
    const [answers, setAnswers] = useState({})
    const [stepError, setStepError] = useState(null)

    const [getQuestions, { isFetching: isCheckingQuestions }] = useLazyGetConnectorQuestionsQuery()
    const { create, isCreating, error: createError } = useCreateConnector(integration, plan)
    const { data: productTypes = EMPTY } = useGetConnectorFeaturesQuery(
        integration && plan.sourceVersion && plan.targetVersion
            ? { connectorId: integration.id, sourceVersion: plan.sourceVersion, targetVersion: plan.targetVersion }
            : skipToken
    )
    const { data: questions = EMPTY } = useGetConnectorQuestionsQuery(isDetails && plan.productVersionId ? plan.productVersionId : skipToken)
    const productType = productTypes.find((item) => item.latestVersion && String(item.latestVersion.id) === plan.productVersionId)

    const partners = useMemo(() => {
        if (!firstSystem) return EMPTY
        const names = [...new Set(integrationList.map((item) => partnerOf(item, firstSystem.shortName)))]
        return names.map(systemFor).sort(bySystemOrder)
    }, [integrationList, firstSystem, systemFor])
    const matches = firstSystem && partnerShortName
        ? integrationList.filter((item) => partnerOf(item, firstSystem.shortName) === partnerShortName)
        : EMPTY
    const chosenMatch = matches.find((item) => String(item.id) === String(draftIntegrationId)) || matches[0] || null

    const updatePlan = useCallback((changes) => setPlan((value) => ({ ...value, ...changes })), [])

    useEffect(() => {
        if (systemParam) setDraftSystemId(systemParam)
    }, [systemParam])

    useEffect(() => {
        setPartnerShortName(null)
        setDraftIntegrationId(null)
    }, [firstId])

    useEffect(() => {
        if (integration && firstSystem) setPartnerShortName(partnerOf(integration, firstSystem.shortName))
    }, [integration, firstSystem])

    useEffect(() => {
        setPlan(EMPTY_PLAN)
        setAnswers({})
    }, [integrationParam])

    useEffect(() => {
        setAnswers((value) => questions.reduce((next, question) => (
            { ...next, [question.id]: value[question.id] !== undefined ? value[question.id] : question.defaultValue || '' }
        ), {}))
    }, [questions])

    useEffect(() => {
        if (isDetails && !plan.productVersionId && integrationParam) {
            history.replace(paths.newConnector({ integration: integrationParam, from }))
        }
    }, [isDetails, plan.productVersionId, integrationParam, from, history])

    useEffect(() => setStepError(null), [current])

    const integrationQuery = from === EGE ? egeQuery : integrationsQuery
    if (current >= STEP.PLAN && (integrationQuery.isLoading || endpointsQuery.isLoading)) {
        return <PageLoader message="Loading integration..." />
    }
    if (current >= STEP.PLAN && !integration) {
        return (
            <PageNotice
                title="Integration not found"
                hint="It may no longer be available."
                linkTo={PATH.NEW_CONNECTOR}
                linkLabel="Start again"
            />
        )
    }

    const firstName = firstSystem ? firstSystem.name : integration ? systemFor(integration.source.shortName).name : null
    const secondName = integration
        ? systemFor(firstSystem ? partnerOf(integration, firstSystem.shortName) : integration.target.shortName).name
        : null
    const steps = [
        { label: 'Choose system', value: current > STEP.SYSTEM ? firstName : null, to: paths.newConnector({}) },
        { label: 'Connect with', value: current > STEP.PARTNER ? secondName : null, to: from === EGE ? PATH.EGE : paths.newConnector({ system: firstId }) },
        { label: 'Versions & features', value: current > STEP.PLAN && productType ? productType.displayName : null, to: paths.newConnector({ integration: integrationParam, from }) },
        { label: 'Details' }
    ]

    const continueFromPlan = async () => {
        setStepError(null)
        try {
            const required = await getQuestions(plan.productVersionId, true).unwrap()
            if (required.length > 0) {
                history.push(paths.newConnector({ integration: integrationParam, from, step: DETAILS }))
                return
            }
            create()
        } catch (error) {
            setStepError('The connector requirements could not be loaded. Please try again.')
        }
    }

    const isBusy = isCheckingQuestions || isCreating
    const isPlanComplete = Boolean(plan.sourceVersion && plan.targetVersion && plan.productVersionId)

    const config = {
        [STEP.SYSTEM]: {
            title: 'Choose a system',
            back: { label: 'Cancel', to: PATH.DASHBOARD },
            next: { label: 'Continue', disabled: !draftSystemId, onClick: () => history.push(paths.newConnector({ system: draftSystemId })) },
            body: (
                <ChooseSystemStep
                    systems={systems}
                    isLoading={endpointsQuery.isLoading}
                    isError={endpointsQuery.isError}
                    onRetry={endpointsQuery.refetch}
                    selectedId={draftSystemId}
                    onSelect={(system) => setDraftSystemId(String(system.id))}
                />
            )
        },
        [STEP.PARTNER]: {
            title: firstSystem ? `Connect ${firstSystem.name} with` : 'Connect with',
            back: { label: 'Back', to: paths.newConnector({}) },
            next: {
                label: 'Continue',
                disabled: !chosenMatch,
                onClick: () => history.push(paths.newConnector({ integration: chosenMatch.id, from: firstId }))
            },
            body: !endpointsQuery.isLoading && !firstSystem ? (
                <p className={Style.message}>This system is not available. Choose another system.</p>
            ) : (
                <ChoosePartnerStep
                    system={firstSystem || fallbackSystem('')}
                    partners={partners}
                    isLoading={endpointsQuery.isLoading || integrationsQuery.isLoading}
                    isError={integrationsQuery.isError}
                    onRetry={integrationsQuery.refetch}
                    partnerShortName={partnerShortName}
                    onSelectPartner={(shortName) => { setPartnerShortName(shortName); setDraftIntegrationId(null) }}
                    matches={matches}
                    integrationId={chosenMatch ? chosenMatch.id : null}
                    onSelectIntegration={setDraftIntegrationId}
                />
            )
        },
        [STEP.PLAN]: {
            title: 'Versions & features',
            back: { label: 'Back', to: steps[STEP.PARTNER].to },
            next: {
                label: isCreating ? 'Creating connector...' : isCheckingQuestions ? 'Please wait...' : 'Continue',
                disabled: !isPlanComplete || isBusy,
                onClick: continueFromPlan
            },
            body: integration ? (
                <>
                    <PairStrip integration={integration} systemFor={systemFor} />
                    <PlanStep integration={integration} plan={plan} onChange={updatePlan} />
                </>
            ) : null
        },
        [STEP.DETAILS]: {
            title: 'Details',
            back: { label: 'Back', onClick: () => history.goBack(), disabled: isBusy },
            next: {
                label: isCreating ? 'Creating connector...' : 'Create connector',
                disabled: !isPlanComplete || isBusy,
                onClick: () => create(answers)
            },
            body: integration ? (
                <>
                    <PairStrip integration={integration} systemFor={systemFor} />
                    <DetailsStep
                        integration={integration}
                        plan={plan}
                        productTypeName={productType ? productType.displayName : null}
                        questions={questions}
                        answers={answers}
                        onAnswer={(id, value) => setAnswers((existing) => ({ ...existing, [id]: value }))}
                    />
                </>
            ) : null
        }
    }[current]

    const error = stepError || createError
    const footer = (
        <>
            {error ? <p className={Style.error} role="alert">{error}</p> : <span />}
            <div className={Style.footerActions}>
                {config.back.to ? (
                    <button type="button" className={Style.secondaryButton} onClick={() => history.push(config.back.to)}>{config.back.label}</button>
                ) : (
                    <button type="button" className={Style.secondaryButton} disabled={config.back.disabled} onClick={config.back.onClick}>{config.back.label}</button>
                )}
                <button type="button" className={Style.primaryButton} disabled={config.next.disabled} onClick={config.next.onClick}>
                    {config.next.label}
                    {isBusy ? null : <i className="uil-arrow-right" aria-hidden="true" />}
                </button>
            </div>
        </>
    )

    return (
        <WizardShell steps={steps} current={current} title={config.title} footer={footer}>
            {config.body}
        </WizardShell>
    )
}

const CreateConnector = () => {
    const { canCreate, isLoading } = useConnectionCreateAccess()
    if (isLoading) {
        return <PageLoader message="Loading..." />
    }
    if (!canCreate) {
        return (
            <PageNotice
                icon="uil-lock-alt"
                title="You don't have permission to create connectors"
                hint="Ask an owner or admin of your account to create it, or to change your role."
                linkTo={PATH.MY_PRODUCTS}
                linkLabel="Go to My Products"
            />
        )
    }
    return <CreateConnectorWizard />
}

export default CreateConnector
